import { Stack, TextArea } from "@carbon/react";
import { FC } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";

import { ConfirmActionModal } from "src/components/Modal";
import { useToast } from "src/components/Toasts/ToastContainer.tsx";
import { mapToModalError } from "src/errors";
import { useUpdateAlertMutation } from "src/graphql/anti-fraud/queries/alerts/mutations/UpdateAlertMutation.ts";
import { useT } from "src/internationalization";

type CommentModal = {
  onClose: () => void;
  alertId: string;
  alertNumber: string;
  comment?: string | null;
};

export type CommentModalPayload = {
  comment?: string | null;
};

export const CommentModal: FC<CommentModal> = ({ onClose, alertId, alertNumber, comment }) => {
  const methods = useForm<CommentModalPayload>({ defaultValues: { comment } });
  const { showSuccessToast } = useToast();
  const t = useT();

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = methods;

  const {
    performMutation: updateAlert,
    loading,
    error,
  } = useUpdateAlertMutation({
    onSuccess: () => {
      if (comment) showSuccessToast(t("page.alert.modal.comment.toast.success.message.update"));
      else showSuccessToast(t("page.alert.modal.comment.toast.success.message.create"));
      onClose();
    },
  });

  const onSubmit: SubmitHandler<CommentModalPayload> = async (data) => {
    await updateAlert({
      id: alertId,
      comment: data.comment ?? "",
    });
  };

  return (
    <ConfirmActionModal
      size="lg"
      error={mapToModalError(error)}
      title={t("page.alert.modal.comment.title")}
      subtitle={t("page.alert.modal.comment.subtitle", { number: alertNumber })}
      loading={loading}
      onClose={onClose}
      onConfirm={handleSubmit(onSubmit)}
      confirmButtonText={t("general.actions.confirm")}
      initialFocus="#comment"
      confirmButtonDisabled={!isDirty}
    >
      <FormProvider {...methods}>
        <Stack gap={5}>
          <TextArea
            invalidText={t("general.form.comment.error.required")}
            invalid={!!errors?.comment}
            autoFocus={false}
            id="comment"
            labelText={t("page.alert.modal.comment.form.comment.label")}
            helperText={t("page.alert.modal.comment.form.comment.helper")}
            {...register("comment", {
              required: t("page.alert.modal.comment.form.comment.invalid"),
            })}
          />
        </Stack>
      </FormProvider>
    </ConfirmActionModal>
  );
};
